import { createActionGroup, emptyProps, props } from "@ngrx/store";

import { ActionSource } from "../state/action-sources";

export const quickJsActions = createActionGroup({
  source: ActionSource.QUICK_JS,
  events: {
    'Fetch Script Success': props<{
      script: string;
    }>(),
    'Fetch Script Error': props<{
      error: Error;
    }>(),
    // `eventId` rides along on both so the duty list can be attributed to the event it describes.
    // Without it a response for event A that lands after the crew has opened event B is
    // indistinguishable from B's own, and would be filed under B.
    'Ops Managers Loaded': props<{
      eventId: string;
      opsManagers: { name: string; phone: string | null }[];
    }>(),
    'Ops Managers Load Error': props<{
      eventId: string;
      error: Error;
    }>(),
    'Load Script Success': emptyProps(),
    'Load Script Error': props<{
      error: Error;
    }>(),
    'Unload Script Success': emptyProps(),
    'Unload Script Error': props<{
      error: Error;
    }>(),
    'Run Calculations Success': props<{
      elements: FormElement[];
      customerElements: FormElement[];
      calculatedState: { [ key: string ]: string | number | boolean },
    }>(),
    'Run Calculations Error': props<{
      error: Error;
    }>(),
    'Input Changed': props<{
      key: string;
      value: any;
    }>(),
    // Set several inputs in one dispatch (e.g. a flag plus its updatedAt companion), so a
    // single conceptual change is one action rather than a sequence of Input Changed dispatches.
    'Inputs Changed': props<{
      inputs: { key: string; value: any }[];
    }>(),
    'Input Reset': props<{ key: string }>(),
    'Calculator Button Clicked': props<{ key: string; action?: string }>(),
    'Reload Script Button Clicked': emptyProps(),
    'Reload Script Command Entered': emptyProps(),
    'Script Url Entered For Job': props<{
      url: string;
    }>(),
    'Set Script On Job Success': props<{
      jobId: string;
      url: string;
    }>(),
    'Set Script On Job Error': props<{
      error: Error;
    }>(),
    'Use Calculator From Zone Settings Success': props<{
      jobId: string;
      url: string;
    }>(),
    'Use Calculator From Zone Settings Error': props<{
      error: Error;
    }>(),

    'Add Charge Requested': props<{
      eventId: string;
      charge: AddChargeFromCalculatorInput,
    }>(),

    'Navigator Step Confirmed': props<{
      stepId: string;
      eventId: string;
      jobId: string;
      /** Wall-clock ms when the new step started */
      stepStartTime: number;
      /** The step ID that was just completed (the one before stepId) */
      previousStepId?: string;
      /** Wall-clock ms when the previous step was confirmed (= stepStartTime of the new step) */
      previousStepCompletedAt?: number;
      /** Wall-clock ms when the previous step started (from store at time of confirm) */
      previousStepStartedAt?: number;
      /**
       * The `navigatorCurrentStep` the store held *before* the optimistic advance (#31), so a
       * failed mutation can put it back. Not the same as `previousStepId`: on a fresh run the
       * persisted value is the `'started'` sentinel while `previousStepId` is a real step id.
       */
      previousPersistedStep?: string | null;
      data?: any;
    }>(),
    'Sync Navigator Step Success': emptyProps(),
    'Sync Navigator Step Error': props<{
      error: Error;
    }>(),
    /**
     * The crew tapped "Back to Step N" inside the undo window — un-write the advance.
     *
     * The advance is persisted the moment Continue is tapped (#46 Stage 2), so undo can no
     * longer just cancel a pending commit; it has to reverse a write that already landed.
     *
     * The contract is an **invariant**, not a best effort: after this lands, the navigator's step
     * bookkeeping — `navigatorCurrentStep`, `navigatorData.steps`, `navigatorData.stepStartTime`
     * and their flattened `navigator_*` mirrors — must be byte-identical to the state before the
     * confirm. Anything left behind is a behaviour change nobody asked for; before Stage 2 an undo
     * wrote nothing at all, so there was no residue to leave.
     *
     * Deliberately **exempt**, because they are not step bookkeeping:
     *   - the crew's inventory edits (`jobs.inventory`, saved via the unsaved-changes queue) and
     *     any job summary they typed — authored work, not a claim about step state. Undoing the
     *     *step* must never un-count items the crew actually counted.
     *   - external side effects: a generated PandaDoc contract, a sent email, a Job comment.
     *   - node-level bookkeeping such as `updatedAt`.
     *   - `stepLocations` — see #44. A geo patch in flight can land after this and re-add an
     *     entry; harmless while nothing reads it, and the append-only reshape that removes the
     *     ambiguity for good belongs with the feature that consumes it.
     */
    'Navigator Step Undone': props<{
      /** The step being returned to. Becomes the persisted `currentStep`. */
      stepId: string;
      eventId: string;
      jobId: string;
      /**
       * The step being abandoned. Sent with all-null timings rather than omitted — a wire signal
       * meaning "delete this step's flattened mirrors", which an omitted key cannot express
       * because the backend never sees the previous JSON. The backend consumes the signal and
       * drops the entry, so nothing is stored for it. Either way it is excluded from the resume
       * path's "started but not completed" test (`job-navigator-page.component.ts:203` requires
       * `startedAt != null`), which matters: an entry keeping a real `startedAt` sorts first and
       * would drop the crew back onto the step they just undid.
       */
      abandonedStepId: string;
      /**
       * The returned step's original `startedAt`, captured before the confirm overwrote it.
       * Restored rather than re-stamped with `Date.now()`: the crew has been on this step since
       * that moment, and re-stamping would silently shorten its reported duration.
       */
      restoredStepStartTime: number;
    }>(),
    'Navigator Step Undone Success': emptyProps(),
    'Navigator Step Undone Error': props<{
      error: Error;
    }>(),
    /**
     * Merge keys into an event's `navigatorData` **without touching `navigatorCurrentStep`**.
     *
     * For components that annotate a run rather than advance it — a photo capture recording its
     * artifact id, an inventory review flushing on unmount. The original case was
     * `PreGenerateDoc` stashing a generated contract's artifact id; that component was deleted in
     * #58 when the contract moved to Aegis, but the reason this action exists outlives it.
     *
     * Such components used to borrow `navigatorStepConfirmed`, whose effect writes `currentStep`,
     * so finishing a background document silently overwrote the crew's real position with a
     * synthetic id (`pregen_<docType>`) that matches no `<Step>`. Resume then landed on step 1
     * with the confirm button dead. See issue #16.
     *
     * A component annotating the run is not the same event as a crew advancing, and the two
     * must not share an action.
     */
    'Navigator Data Patched': props<{
      eventId: string;
      patch: Record<string, any>;
    }>(),
    'Patch Navigator Data Success': emptyProps(),
    'Patch Navigator Data Error': props<{
      error: Error;
    }>(),
    /**
     * The crew pressed "Confirm Scope with Customer" in the navigator's inventory editor.
     *
     * Sends the inventory edits the crew made during the step as one navigator save through the
     * outbox (#86c), with the review marks: the server merges them item by item into the job's
     * inventory, so they wait for signal and never silently overwrite an office edit. It used to
     * ride the job page's save, which had no way to wait and replaced the whole list.
     *
     * Saving here rather than per edit is deliberate. The server pushes an `updated inventory`
     * history item on every write of `jobs.inventory`, so a save per tick or per quantity tap would
     * put dozens of lines on the job's history for one move. One confirmation, one save, one entry.
     */
    'Navigator Scope Confirm Button Clicked': props<{
      eventId: string;
      /** The review marks as they stand at the confirm — a copy, never the component's live object. */
      inventoryReview: Record<string, unknown>;
    }>(),
    /**
     * The crew's inventory save landed and the editor holds the merged list (#86c): save the
     * calculator figures the phone recalculated from it, with a regular job save that leaves the
     * inventory out.
     */
    'Navigator Inventory Figures Save Requested': props<{
      jobId: string;
    }>(),
    /**
     * Restore navigator state from the persisted event on page load.
     *
     * `stepId` is null on a fresh start — nothing has been confirmed yet, so there is no
     * step to restore, but `stepStartTime` still needs restoring so the first step's
     * elapsed time survives a reload. Consumers already ignore an unknown step id.
     */
    'Navigator State Restored': props<{
      stepId: string | null;
      stepStartTime: number;
    }>(),
    /**
     * An authority outside this navigator run has moved the crew's step.
     *
     * The *only* sanctioned way for external state to move a mounted navigator. Everything else
     * that moves the step is the crew themselves — `navigatorStepConfirmed`, `navigatorStepUndone`
     * — or a one-shot resume at mount (`navigatorStateRestored`).
     *
     * Why this exists as its own action rather than falling out of a subscription: until #46 was
     * reworked, the page re-derived the crew's step from `selectJob` on every job change. That is a
     * shared object written by echoes, refetches, optimistic patches and ops alike, and its
     * `stepStartTime` comes from `navigatorData`, so *every inventory patch re-triggered it*. Six
     * rounds of guards tried to make that object trustworthy. It cannot be: a `jobChanged` echo
     * describes what is **persisted**, and a crew with a write in flight is legitimately ahead of
     * that — measured at 30-80s behind on a throttled run, walking the crew back two or three steps
     * with a payload that was entirely correct about the database. See `docs/crew-navigator.md`.
     *
     * So the conduit is closed and this is the door left in it. Ops moving a crew from the office is
     * a required flow, and it now has to say so explicitly.
     *
     * `eventId` is mandatory and checked by the reducer: the slice holds one step for the whole
     * session, so an override aimed at a different event must not land on this run (#30).
     *
     * Dispatched by:
     *   - `remoteNavigatorStepOverride` (`job-navigator.effects.ts`) for another machine's write
     *   - the Update Navigator dialog's own success handler, for the same tab
     *
     * Caveat (#52): a second crew device's write is indistinguishable from an ops override on the
     * wire — both are simply "not this session". Whoever handles this action should therefore make
     * it *visible* rather than silent, so a collision is reported rather than mistaken for a bug.
     */
    'Navigator Step Overridden': props<{
      eventId: string;
      stepId: string;
      stepStartTime: number;
    }>(),
    /**
     * The navigator page has opened for an event. Dispatched as the very first thing in
     * `ngOnInit`, before any restore subscription is established.
     *
     * `navigatorCurrentStepId` is a single value for the whole session, not a map keyed by
     * event. The old modal's `openNavigator` action carried a guard for this, but nothing ever
     * dispatched it, so `navigatorActiveEventId` stayed `null` and the guard never ran — which
     * let job A's step be picked up when job B's navigator opened (#30). This action scopes the
     * slice to one event. (`openNavigator` and the rest of the modal's wiring were removed in #60.)
     *
     * Ordering matters: dispatching before the restore subscription means a genuine restore for
     * the newly-opened event lands *after* the clear and survives it.
     */
    'Navigator Opened For Event': props<{
      eventId: string;
    }>(),
    /**
     * Ops has reset the navigator for an event from the Update Navigator dialog.
     *
     * The current step lives in two places — `navigatorCurrentStep` on the event in the jobs
     * slice, and `navigatorCurrentStepId` here — and they are two representations of one fact,
     * so they have to be cleared together. Reset used to patch only the jobs slice, which left
     * the button label saying "Start Navigator" while the navigator still reopened on the old
     * step, until a page reload dropped this slice. See issue #26.
     */
    'Navigator Reset': props<{
      eventId: string;
    }>(),
    /**
     * Dispatched when the crew confirms the final step of the navigator.
     * Triggers: mark event as completed, persist final step timing, navigate back.
     */
    'Navigator Completed': props<{
      eventId: string;
      /** Timing for the last step that was just completed */
      lastStepId: string;
      lastStepStartedAt: number;
      lastStepCompletedAt: number;
      navigatorData: string;
    }>(),
    'Navigator Complete Success': emptyProps(),
    'Navigator Complete Error': props<{ error: Error }>(),

    /**
     * The crew has stopped typing in a notes editor inside the navigator, so the notes
     * should be written to the job.
     *
     * Notes typed in the navigator reach the `summaryEditors` store slice via
     * `JobSummaryActions.summaryEditorContentChanged`, but nothing there writes them to the
     * database: the only effect that does (`buildSaveChangesVariables`) fires on explicit
     * save actions from the job tool, and the navigator is a full-screen crew surface with
     * no save button. Emitted by `requestNavigatorNotesSave`. See issue #14.
     */
    'Navigator Notes Save Requested': props<{
      summaryKey: 'crewSummary' | 'adminSummary' | 'customerSummary';
    }>(),
    'Save Navigator Notes Success': props<{ changeIds: string[] }>(),
    'Save Navigator Notes Error': props<{ error: Error }>(),
    // Requirement: `#6951` - Crew Pay boxes saved from a zone the job is shared with. `inputs`
    // holds the calculator inputs as saved, keyed like `userInputs`.
    'Save Shared Crew Pay Started': props<{ changeIds: string[] }>(),
    'Save Shared Crew Pay Success': props<{ changeIds: string[]; inputs: Record<string, boolean> }>(),
    'Save Shared Crew Pay Error': props<{ error: Error; changeIds: string[] }>(),
  },
})
